import cn from "classnames";
import { useRouter } from "next/router";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import { BaseButton } from "@/src/component/base/BaseButton";
import { DrawerWithExit } from "@/src/component/base/DrawerWithExit";
import { SectionModule, TextBreakpointModule } from "@/src/component/partial/__module__";
import { BuildDynamicForm } from "@/src/component/partial/BuildDynamicForm";
import { KEYS } from "@/src/const";
import { StyleHooks } from "@/src/hook";
import { ComponentType, StrapiComponentType } from "@/src/type";

import styles from "./DynamicForm.module.scss";

interface DynamicFormProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.DynamicForm> {}

function DynamicForm({ section, slug, snapToGrid, classes, searchParams, lazy, quality }: DynamicFormProps) {
  const form: StrapiComponentType.Section.DynamicForm = section.type?.[0];
  const [openForm, setOpenForm] = useState<boolean>(false);
  const params = useSearchParams();
  const router = useRouter();
  const contact = params?.get("contact");
  const isSmall = StyleHooks.useBreakPointDown("sm");
  const title = form?.steps?.[0]?.title ?? section.title;
  const description = form?.steps?.[0]?.subtitle;
  const noBackground = !section?.background?.color && (section?.background?.imageBreakpoints ?? []).length === 0;
  const desktopIsDark = section?.background?.color === "secondaryMain" || noBackground;

  useEffect(() => {
    if (contact === "auto" && isSmall) {
      setOpenForm(true);
      const params = new URLSearchParams(window.location.search);
      params.delete("contact");
      const pathname = window.location.pathname;
      router.push(`${pathname}?${params.toString()}`, undefined, { shallow: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, isSmall]);

  const metadataRecord =
    form.metadata?.reduce<Record<string, string>>((acc, item) => {
      if (item.key && item.value) {
        acc[item.key] = item.value;
      }
      return acc;
    }, {}) || {};

  const handleOnSuccess = () => {
    localStorage.setItem(KEYS.LOCAL_STORAGE.NOTIFICATION, "true");
    localStorage.setItem(KEYS.LOCAL_STORAGE.NOTIFICATION_COUNTER, "0");
  };

  return (
    <SectionModule
      section={section}
      slug={slug}
      stylePrefix="Form"
      styleContainerId={form.id}
      snapToGrid={snapToGrid}
      classes={{
        section: classes?.section,
      }}
      searchParams={searchParams}
      lazy={lazy}
      quality={quality}
    >
      <>
        <div className={styles.container}>
          <div className={styles.textContainer}>
            <TextBreakpointModule
              id={`form_title_${form.id}`}
              text={title}
              className={styles.title}
            />
            <TextBreakpointModule
              id={`form_desc_${form.id}`}
              text={description}
              className={styles.description}
            />
          </div>
          <div className={cn(styles.formContainer, form.noContactUsButtonMobile ? styles.noContactUsButtonMobile : undefined)}>
            <BuildDynamicForm
              form={form}
              onSuccess={handleOnSuccess}
              formId={form.crmFormLabel ?? "DesktopLeadForm"}
              disableAnimatedLabel={!desktopIsDark}
              metadata={metadataRecord}
              isDark={desktopIsDark}
            />
          </div>
          <div className={cn(styles.formOpen, form.noContactUsButtonMobile ? styles.noContactUsButtonMobile : undefined)}>
            <BaseButton
              type="submit"
              variant="outlined"
              color="secondary"
              size="large"
              onClick={() => setOpenForm(true)}
            >
              {form.contactUsButtonLabel}
            </BaseButton>
          </div>
          <DrawerWithExit
            drawer={{
              open: openForm,
              onClose: () => setOpenForm(!openForm),
            }}
          >
            <div className={styles.modalContainer}>
              <div className={styles.modalForm}>
                <div className={styles.textContainer}>
                  <TextBreakpointModule
                    id={`form_title_drawer_${form.id}`}
                    text={title}
                    className={cn(styles.title, styles.dark)}
                  />
                  <TextBreakpointModule
                    id={`form_title_drawer_${form.id}`}
                    text={description}
                    className={cn(styles.description, styles.dark)}
                  />
                </div>
                <BuildDynamicForm
                  form={form}
                  onSuccess={handleOnSuccess}
                  formId={form.crmFormLabel ?? "MobileLeadForm"}
                  isDark={true}
                  metadata={metadataRecord}
                />
              </div>
            </div>
          </DrawerWithExit>
        </div>
      </>
    </SectionModule>
  );
}

export default DynamicForm;
